/*
 * ****************************************************************************
 * Copyright (C) 2018-2018, All rights reserved.
 * Project: chessmate
 * Modified By: rogerk
 * Created On: March 29th, 2018
 * Last Modified: March 29th, 2018
 * ****************************************************************************
 */
import * as React from 'react';
import FenParser from '@chess-fu/fen-parser';
import { ChessGame as Chess, Move } from '@chess-fu/chess-game';
import * as queryString from 'query-string';
import { EventEmitter } from 'events';

const { Provider, Consumer } = React.createContext<GameState>(null as any);

export const GameStateContextProvider = Provider;
export const GameStateContext = Consumer;

/* Initial game positions... */
const game_states = {
  start: 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1',
  empty: '8/8/8/8/8/8/8/8 w - - 0 1'
};

export type MoveHistory = { [key: number]: string };

export interface UriGameState {
  fen: 'start' | 'empty' | string;
  play: MoveHistory;
  pos?: number;
  options?: any;
}

/* game state mechanics */
export class GameState {
  private readonly _events: EventEmitter;
  private readonly _game: Chess;
  private _pos: number;
  private _moves: MoveHistory;

  constructor(game?: Chess) {
    this._events = new EventEmitter();
    this._game = game || new Chess(game_states.start);
    this._moves = {};
    this._pos = 0;
  }

  get game(): Chess { return this._game; }

  fen(): string { return this._game.fen(); }
  get position(): number { return this._pos; }
  get lastPosition(): number { return Object.keys(this._moves).length; }

  onChange(subscriber: (arg: { fen: string, position: number }) => void): () => void {
    this._events.on('change', subscriber);
    return () => this._events.removeListener('change', subscriber);
  }

  onChanged(source: string) {
    this._events.emit('change', { fen: this.fen(), position: this._pos, source });
  }

  toUriState(options?: any): UriGameState {
    const params: UriGameState = {
      fen: this.startingFEN(),
      play: { ...this._moves },
      options: options || {}
    };
    if (this._pos !== Object.keys(this._moves).length) { params.pos = this._pos; }
    return params;
  }

  static toUri(state: UriGameState, options?: any): string {
    const params: any = { ...state.play, ...state.options, ...options };
    if (typeof params.pos === 'number') { params.pos = state.pos; }
    return `${state.fen}?${queryString.stringify(params)}`.replace(/\s+/g, '.');
  }

  /** Returns a state bag to restore game data */
  static fromUri(path: string, search?: string): UriGameState {
    if (!search && path.indexOf('?')) {
      [path, search] = path.split('?', 2);
    }

    const params = queryString.parse(search || '');
    const moveHistory: string[] = [];
    for (let ix = 0; params[ix]; ix++) {
      moveHistory.push(params[ix]);
      delete params[ix];
    }

    const state: UriGameState = {
      fen: (path || 'start').replace(/\.+/g, ' '),
      play: moveHistory.reduce<MoveHistory>((o, m, ix) => { o[ix] = m; return o; }, {})
    };

    if (params.pos) {
      const pos = parseInt(params.pos, 10);
      if (pos >= 0 && pos < 65535) {
        state.pos = pos;
        delete params.pos;
      }
    }
    state.options = params;
    return state;
  }

  reset(empty?: boolean) {
    this.initGame(empty ? game_states.empty : game_states.start);
    this.onChanged('reset');
  }

  startingFEN(): string {
    const headers: any = this._game.header();
    return (parseInt(headers.SetUp || '0', 10) && headers.FEN) ? headers.FEN : 'start';
  }

  pgn() {
    const pgn = this._game.pgn({ newline_char: '\n', max_width: 80 });
    const history = this._game.history();
    console.log({pgn, history});
    return pgn;
  }

  loadPgn(pgn: string) {
    this._game.loadPgn(pgn);
    this._moves = {};
    const history = this._game.history();
    for (let ix = 0; ix < history.length; ix++) {
      this._moves[ix] = history[ix].san;
      this._pos = ix + 1;
    }
  }

  allMoves(): Move[] {
    return this._game.moves({ verbose: true }) as Move[];
  }

  getMove(position: number): Move | undefined {
    const history = this._game.history({ verbose: true });
    if (position >= 0 && position < history.length) {
      return history[position];
    }
    return;
  }

  moveToPosition(position: number) {
    const moveCount = Object.keys(this._moves).length;
    if (position === this._pos) {
      return true;
    }

    if (position >= 0 && position <= moveCount) {
      if (this._pos < position) {
        for (; this._pos < position; this._pos++) {
          this.game.move(this._moves[this._pos]);
        }
      }
      else {
        for (; this._pos > position; this._pos--) {
          this.game.undo();
        }
      }

      this.onChanged('seek');
      return true;
    }

    return false;
  }

  getSameGamePos(state: UriGameState): undefined | boolean {
    const startFEN = game_states[state.fen] || state.fen;
    const prevStart = this.startingFEN();
    const prevStartFEN = game_states[prevStart] || prevStart;
    if (startFEN !== prevStartFEN) {
      return;
    }

    const newMoves = state.play || {};
    const newCount = Object.keys(newMoves).length;
    const myCount = Object.keys(this._moves).length;

    if (myCount < newCount) { return; }

    for (let ix = 0; ix < newCount; ix++) {
      if (newMoves[ix] !== this._moves[ix]) { return; }
    }
    return this.moveToPosition(newCount);
  }

  setGameState(state: UriGameState) {
    const startFEN = game_states[state.fen] || state.fen;

    if (this.getSameGamePos(state)) {
      return;
    }

    console.warn(`Game loaded.`, state);
    this.initGame(startFEN);
    this._moves = state.play || {};
    this._pos = state.pos || Object.keys(this._moves).length;

    for (let i = 0; this._moves[i]; i++) {
      if (i >= this._pos) break;
      try { this.game.move(this._moves[i]); }
      catch (ex) {
        console.error(`Invalid move: ${i} ${this._moves[i]}`);
        break;
      }
    }

    //this.game.isOngoing();
    this.onChanged('set');
  }

  initGame(fen: string) {
    const startFEN = game_states[fen] || fen || game_states.start;
    this._moves = {};
    this._pos = 0;
    this._game.load(startFEN);

    const twoDigit = (x: number) => `${x < 10 ? '0' : ''}${x}`;
    const now = new Date();

    this._game.header(...[
      'Event', 'Casual Game',
      'Site', 'chess-fu.com',
      'Mode', 'ICS',
      'Date', `${now.getFullYear()}.${twoDigit(now.getMonth() + 1)}.${twoDigit(now.getDate())}`,
      'Time', `${twoDigit(now.getHours())}:${twoDigit(now.getMinutes())}:${twoDigit(now.getSeconds())}`,
      'UTCDate', `${now.getUTCFullYear()}.${twoDigit(now.getUTCMonth() + 1)}.${twoDigit(now.getUTCDate())}`,
      'UTCTime', `${twoDigit(now.getUTCHours())}:${twoDigit(now.getUTCMinutes())}:${twoDigit(now.getUTCSeconds())}`,
      'Round', '1',
      'White', '-',
      'Black', '-'
    ]);
  }

  getPiece(square: string): string | null {
    return this._game.get(square);
  }

  apply(changes: { [key: string]: string | null }) {
    for (const key of Object.keys(changes || {})) {
      const piece = changes[key];
      if (piece) {
        this._game.set(key, piece);
      }
      else {
        this._game.remove(key);
      }
    }
    const startFEN = this._game.fen();
    this.initGame(startFEN);
    this.onChanged('apply');
  }

  setPlayer(player: string) {
    const fen = new FenParser(this.fen());
    fen.turn = player;
    const startFEN = fen.toString();
    this.initGame(startFEN);
    this.onChanged('apply');
  }

  setCastles(castles: string) {
    const fen = new FenParser(this.fen());
    fen.castles = castles || '-';
    const startFEN = fen.toString();
    this.initGame(startFEN);
    this.onChanged('apply');
  }

  move(move: Move) {
    const moveCount = Object.keys(this._moves).length;
    //clear any backed out moves...
    if (this._pos < moveCount) {
      for (let ix = this._pos; this._moves[ix]; ix++) {
        delete this._moves[ix];
      }
    }

    move = this._game.move(move);
    const moveIx = this._pos++;
    this._moves[moveIx] = move.san || '...';

    this.onChanged('move');
    return move;
  }

  movesText(): string {
    const results = [];
    const startPos = this.startingFEN();
    const startFEN = game_states[startPos] || startPos;

    const fenInfo = new FenParser(startFEN);
    const blackFirst = (fenInfo.turn === 'b');

    const movesCount = Object.keys(this._moves).length;
    let moveIx = 1;
    for (let ix = 0; ix < movesCount; ix++) {
      const line = [`${moveIx++}. `];

      line.push(this._pos === ix ? '>' : '');
      if (ix === 0 && blackFirst) { line.push('...'); }
      else { line.push(this._moves[ix]); ix++; }

      if (ix < movesCount) {
        line.push(' ');
        line.push(this._pos === ix ? '>' : '');
        line.push(this._moves[ix]);
      }
      results.push(line.join(''));
    }
    return results.join('\n');
  }
}


// WEBPACK FOOTER //
// ./src/contexts/gameStateContext.tsx